/**
 * useRealtimeAudioPlayback
 *
 * Gapless PCM16 audio playback for Azure OpenAI Realtime responses.
 *
 * Azure Realtime sends spoken AI responses as a stream of
 * `response.audio.delta` events containing Base64-encoded 24 kHz
 * mono PCM16 chunks.  This hook:
 *   - Decodes each chunk (Base64 → Int16 → Float32)
 *   - Schedules contiguous AudioBufferSourceNodes so there are no gaps
 *     or clicks between chunks
 *   - Exposes `enqueueChunk`, `stopPlayback`, and `isPlaying`
 *
 * When `enabled` is false the hook is a no-op (no AudioContext created).
 */
import { logger } from '@/lib/logger';

import { useCallback, useEffect, useRef, useState } from 'react';

// ── Constants ──────────────────────────────────────────────────────────────

/** Azure OpenAI Realtime audio output sample-rate */
const SAMPLE_RATE = 24_000;

// ── Types ──────────────────────────────────────────────────────────────────

export interface UseRealtimeAudioPlaybackOptions {
  /** When false the hook is completely disabled (no AudioContext created). */
  enabled: boolean;
}

export interface UseRealtimeAudioPlaybackResult {
  /**
   * Enqueue a Base64-encoded PCM16 audio chunk for gapless playback.
   * No-op when `enabled` is false.
   */
  enqueueChunk: (base64Pcm16: string) => void;
  /**
   * Immediately stop all queued and playing audio.
   * Resets the internal playback clock so the next enqueued chunk plays at once.
   */
  stopPlayback: () => void;
  /** True while audio is being played or chunks are queued. */
  isPlaying: boolean;
}

// ── Helper: Base64 PCM16 → Float32Array ────────────────────────────────────

function decodePcm16(base64: string): Float32Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  // PCM16 is little-endian 16-bit signed integers
  const int16 = new Int16Array(bytes.buffer);
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i++) {
    float32[i] = int16[i] / 32_768;
  }
  return float32;
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useRealtimeAudioPlayback(
  options: UseRealtimeAudioPlaybackOptions,
): UseRealtimeAudioPlaybackResult {
  const { enabled } = options;

  const [isPlaying, setIsPlaying] = useState(false);

  // Lazy AudioContext — created on first enqueue when enabled.
  const audioCtxRef = useRef<AudioContext | null>(null);

  /**
   * nextStartTimeRef tracks the scheduled end-time of the last queued buffer
   * so we can schedule the next chunk immediately after it without gaps.
   */
  const nextStartTimeRef = useRef<number>(0);

  /**
   * Active AudioBufferSourceNodes so we can stop them all on stopPlayback().
   */
  const activeNodesRef = useRef<Set<AudioBufferSourceNode>>(new Set());

  // ── Lazy AudioContext access ──────────────────────────────────────────────

  const getAudioContext = useCallback((): AudioContext | null => {
    if (!enabled) return null;
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new AudioContext({ sampleRate: SAMPLE_RATE });
    }
    // Resume if suspended (browser autoplay policy)
    if (audioCtxRef.current.state === 'suspended') {
      void audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, [enabled]);

  // ── enqueueChunk ─────────────────────────────────────────────────────────

  const enqueueChunk = useCallback(
    (base64Pcm16: string) => {
      const ctx = getAudioContext();
      if (!ctx) return;

      let float32: Float32Array;
      try {
        float32 = decodePcm16(base64Pcm16);
      } catch (err) {
        logger.warn('useRealtimeAudioPlayback', 'Failed to decode PCM16 chunk', err);
        return;
      }

      if (float32.length === 0) return;

      // Create an AudioBuffer and copy the float samples into it.
      const buffer = ctx.createBuffer(1, float32.length, SAMPLE_RATE);
      buffer.copyToChannel(float32 as Float32Array<ArrayBuffer>, 0);

      // Schedule the source node to play right after the previous chunk ends.
      const startTime = Math.max(ctx.currentTime, nextStartTimeRef.current);
      nextStartTimeRef.current = startTime + buffer.duration;

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);

      activeNodesRef.current.add(source);
      setIsPlaying(true);

      source.onended = () => {
        activeNodesRef.current.delete(source);
        if (activeNodesRef.current.size === 0) {
          setIsPlaying(false);
        }
      };

      source.start(startTime);
    },
    [getAudioContext],
  );

  // ── stopPlayback ─────────────────────────────────────────────────────────

  const stopPlayback = useCallback(() => {
    for (const node of activeNodesRef.current) {
      try { node.stop(); } catch { /* already stopped */ }
    }
    activeNodesRef.current.clear();
    nextStartTimeRef.current = 0;
    setIsPlaying(false);
  }, []);

  // ── Cleanup on unmount or when disabled ──────────────────────────────────

  useEffect(() => {
    if (!enabled) {
      stopPlayback();
    }
  }, [enabled, stopPlayback]);

  useEffect(() => {
    return () => {
      stopPlayback();
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        void audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { enqueueChunk, stopPlayback, isPlaying };
}
